import { useQuery } from '../models/reactUtils'
import { observer } from 'mobx-react'
import React from 'react'

import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded'
import InfoIcon from '@mui/icons-material/Info'
import LanguageIcon from '@mui/icons-material/Language'
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded'
import Grid from '@mui/material/Grid'
import Link from '@mui/material/Link'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import { useTheme } from '@mui/material/styles'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'

import { AppointmentButton } from './AppointmentButton'
import ProviderItem from './ProviderItem'

export const LocationItem = observer(({ location, provider, toggleDrawer }) => {
  const { store } = useQuery()
  const theme = useTheme()
  const desktop = useMediaQuery(theme.breakpoints.up('md'))
  const focusLocation = () => {
    if (!desktop) {
      toggleDrawer(false)
    }
    store.locationStore.setFocusLocation(location)
  }

  const isSelected =
    store.locationStore.focusLocation &&
    store.locationStore.focusLocation.id === location.id

  // Render nothing if location undefined
  if (!location) {
    return null
  }

  return (
    <ListItem
      divider
      key={location.id}
      alignItems="flex-start"
      disableGutters={true}
      sx={{ p: 0 }}
    >
      <ListItemButton
        selected={isSelected}
        onClick={focusLocation}
        sx={{ px: 3, py: 2 }}
      >
        <ListItemText
          disableTypography
          primary={
            <Grid container direction="column">
              <Typography variant="h5" color="secondary">
                {location.displayName}
              </Typography>
              <Grid item container justifyContent="space-between" wrap="nowrap">
                <Grid
                  item
                  container
                  direction="column"
                  width={3 / 4}
                  sx={{ mt: 1 }}
                >
                  <Grid container>
                    <Grid item sx={{ mr: 1 }}>
                      <BusinessRoundedIcon fontSize="small" />
                    </Grid>
                    <Grid item>
                      <Link
                        color="inherit"
                        underline="always"
                        onClick={focusLocation}
                      >
                        <Typography variant="caption">
                          {location.street},
                        </Typography>
                        <Typography
                          variant="caption"
                          gutterBottom
                          sx={{ display: 'block' }}
                        >
                          {location.city} {location.state} {location.zip}
                        </Typography>
                      </Link>
                    </Grid>
                  </Grid>
                  <Grid container alignItems="center">
                    <Grid item sx={{ mr: 1 }}>
                      <PhoneRoundedIcon fontSize="small" />
                    </Grid>
                    <Grid item>
                      <Link
                        color="secondary"
                        underline="always"
                        href={`tel:${location.formattedPhoneNumber}`}
                      >
                        <Typography variant="caption">
                          {location.formattedPhoneNumber}
                        </Typography>
                      </Link>
                    </Grid>
                  </Grid>
                  <Grid container alignItems="center">
                    <Grid item sx={{ mr: 1 }}>
                      <LanguageIcon fontSize="small" />
                    </Grid>
                    <Grid item>
                      <Typography variant="caption">
                        {location.formattedLanguagesSpoken}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid container direction="column" sx={{ mt: 1.5 }}>
                {location.partnerTags &&
                  location.partnerTags.map(partnerTag => (
                    <Tooltip
                      arrow
                      key={partnerTag.id}
                      placement="top-start"
                      title={
                        <>
                          <Typography color="inherit">
                            {partnerTag.name}
                          </Typography>
                          {partnerTag.description}
                        </>
                      }
                      sx={{ maxWidth: 220, bgcolor: '#f9fbfc' }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignSelf: 'flex-start',
                          mb: 0.5,
                        }}
                      >
                        {partnerTag.name}{' '}
                        <InfoIcon
                          style={{
                            color: partnerTag.hexColor,
                            fontSize: '1rem',
                          }}
                        />
                      </Typography>
                    </Tooltip>
                  ))}
              </Grid>
            </Grid>
          }
          secondary={
            <Grid container direction="column">
              {location.providers &&
                location.providers.map(provider => (
                  <ProviderItem key={provider.id} provider={provider} />
                ))}
              {location.isClaimed && (
                <AppointmentButton location={location} provider={provider} />
              )}
            </Grid>
          }
        />
      </ListItemButton>
    </ListItem>
  )
})

LocationItem.displayName = 'LocationItem'

export default LocationItem
